<template>
  <div class="realtime-screen">
    <div class="screen-header">
      <div class="header-left">
        <el-select v-model="websiteId" placeholder="全部网站" clearable size="small" style="width:160px" popper-class="realtime-dark-popper" @change="refreshAll">
          <el-option label="全部网站" :value="0" />
          <el-option v-for="w in websites" :key="w.id" :label="w.name" :value="w.id" />
        </el-select>
      </div>
      <div class="header-center">
        <h1 class="screen-title">实时监控大屏</h1>
        <div class="screen-time">{{ currentTime }}</div>
      </div>
      <div class="header-right">
        <span class="status-dot" :class="{ active: isConnected }"></span>
        <span class="status-text">{{ isConnected ? '实时连接' : '连接中...' }}</span>
        <el-select v-model="refreshInterval" size="small" style="width:100px; margin-left:12px" popper-class="realtime-dark-popper" @change="onIntervalChange">
          <el-option label="5秒" :value="5000" />
          <el-option label="10秒" :value="10000" />
          <el-option label="30秒" :value="30000" />
          <el-option label="手动" :value="0" />
        </el-select>
      </div>
    </div>

    <div class="screen-body">
      <div class="screen-left">
        <div class="metric-row">
          <div class="metric-card">
            <div class="metric-value">{{ overview.today_visits || 0 }}</div>
            <div class="metric-label">今日访问量</div>
            <div class="metric-sub">较昨日 {{ yesterdayCompareVisits }}</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ overview.today_visitors || 0 }}</div>
            <div class="metric-label">今日访客</div>
            <div class="metric-sub">独立访客数</div>
          </div>
        </div>
        <div class="metric-row">
          <div class="metric-card clickable" @click="showOnlineDialog">
            <div class="metric-value highlight">{{ overview.online_visitors || 0 }}</div>
            <div class="metric-label">在线访客</div>
            <div class="metric-sub">最近5分钟活跃 · 点击查看</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ overview.today_new_visitors || 0 }}</div>
            <div class="metric-label">今日新访客</div>
            <div class="metric-sub">首次访问用户</div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header"><span class="panel-title">实时地域分布</span></div>
          <div class="panel-body">
            <div ref="locationChart" class="chart-area"></div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header"><span class="panel-title">实时热门页面</span></div>
          <div class="panel-body hot-pages">
            <div v-for="(page, idx) in hotPages" :key="idx" class="hot-page-item">
              <span class="hot-page-rank" :class="'rank-' + (idx + 1)">{{ idx + 1 }}</span>
              <span class="hot-page-url" :title="page.page_path">{{ formatUrl(page.page_path) }}</span>
              <span class="hot-page-count">{{ page.visit_count }}</span>
            </div>
            <div v-if="hotPages.length === 0" class="empty-tip">暂无数据</div>
          </div>
        </div>
      </div>

      <div class="screen-center">
        <div class="panel-card trend-panel">
          <div class="panel-header">
            <span class="panel-title">实时访问趋势</span>
            <div class="panel-tabs">
              <span :class="{ active: trendMinutes === 30 }" @click="trendMinutes = 30; loadTrend()">30分钟</span>
              <span :class="{ active: trendMinutes === 60 }" @click="trendMinutes = 60; loadTrend()">1小时</span>
              <span :class="{ active: trendMinutes === 120 }" @click="trendMinutes = 120; loadTrend()">2小时</span>
            </div>
          </div>
          <div class="panel-body">
            <div ref="trendChart" class="chart-area trend-chart"></div>
          </div>
        </div>

        <div class="panel-card flow-panel">
          <div class="panel-header">
            <span class="panel-title">实时访问流</span>
            <span class="flow-count">共 {{ recentList.length }} 条</span>
          </div>
          <div class="panel-body flow-list" ref="flowList">
            <div v-for="item in recentList" :key="item.id" class="flow-item" :class="{ new: isNewItem(item.id) }">
              <div class="flow-avatar">
                <i :class="getDeviceIcon(item.device)"></i>
              </div>
              <div class="flow-info">
                <div class="flow-top">
                  <span class="flow-ip">{{ item.ip }}</span>
                  <el-tag v-if="blockedIps.has(item.ip)" size="mini" type="danger" effect="dark" style="margin:0 4px">已禁用</el-tag>
                  <span class="flow-location" v-if="item.ip_location">{{ item.ip_location }}</span>
                  <el-tag v-if="item.is_new_visitor" size="mini" type="success" class="flow-tag">新访客</el-tag>
                </div>
                <div class="flow-bottom">
                  <span class="flow-page" :title="item.page_url">{{ item.page_url }}</span>
                  <span class="flow-time">{{ formatTime(item.created_at) }}</span>
                </div>
              </div>
            </div>
            <div v-if="recentList.length === 0" class="empty-tip">暂无实时数据</div>
          </div>
        </div>
      </div>

      <div class="screen-right">
        <div class="metric-row">
          <div class="metric-card">
            <div class="metric-value">{{ overview.today_ips || 0 }}</div>
            <div class="metric-label">今日IP数</div>
            <div class="metric-sub">独立IP地址</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ overview.hour_visits || 0 }}</div>
            <div class="metric-label">当前小时</div>
            <div class="metric-sub">访问量</div>
          </div>
        </div>
        <div class="metric-row">
          <div class="metric-card">
            <div class="metric-value">{{ overview.yesterday_visits || 0 }}</div>
            <div class="metric-label">昨日访问量</div>
            <div class="metric-sub">对比参考</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ overview.online_ips || 0 }}</div>
            <div class="metric-label">在线IP</div>
            <div class="metric-sub">最近5分钟</div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header"><span class="panel-title">访问来源域名</span></div>
          <div class="panel-body referer-list">
            <div v-for="(ref, idx) in topReferers" :key="idx" class="referer-item">
              <span class="referer-rank">{{ idx + 1 }}</span>
              <span class="referer-domain">{{ ref.referer_domain || '直接访问' }}</span>
              <span class="referer-count">{{ ref.count }}</span>
            </div>
            <div v-if="topReferers.length === 0" class="empty-tip">暂无数据</div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header"><span class="panel-title">设备类型分布</span></div>
          <div class="panel-body">
            <div ref="deviceChart" class="chart-area device-chart"></div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      title="在线访客列表"
      :visible.sync="onlineDialogVisible"
      width="720px"
      append-to-body
      custom-class="online-dialog"
    >
      <div class="online-dialog-body">
        <div class="online-dialog-info">
          <span>最近5分钟活跃 · 共 {{ onlineVisitors.length }} 位访客</span>
          <span class="online-dialog-time">查询时间: {{ onlineQueryTime }}</span>
        </div>
        <div class="online-dialog-list" v-loading="onlineLoading">
          <div v-for="(visitor, idx) in onlineVisitors" :key="idx" class="online-visitor-item">
            <div class="online-visitor-avatar">
              <i :class="getDeviceIcon(visitor.device)"></i>
            </div>
            <div class="online-visitor-info">
              <div class="online-visitor-top">
                <span class="online-visitor-ip">{{ visitor.ip }}</span>
                <el-tag v-if="blockedIps.has(visitor.ip)" size="mini" type="danger" effect="dark" style="margin:0 4px">已禁用</el-tag>
                <span class="online-visitor-location" v-if="visitor.ip_location">{{ visitor.ip_location }}</span>
                <span class="online-visitor-count">{{ visitor.visit_count }}次访问</span>
              </div>
              <div class="online-visitor-bottom">
                <span class="online-visitor-page" :title="visitor.page_url">{{ visitor.page_url }}</span>
                <span class="online-visitor-time">{{ formatFullTime(visitor.last_visit) }}</span>
              </div>
              <div class="online-visitor-meta">
                <span>{{ visitor.device || '未知' }}</span>
                <span v-if="visitor.browser"> · {{ visitor.browser }}</span>
                <span v-if="visitor.os"> · {{ visitor.os }}</span>
                <span v-if="visitor.website_name"> · {{ visitor.website_name }}</span>
              </div>
            </div>
          </div>
          <div v-if="onlineVisitors.length === 0 && !onlineLoading" class="online-empty">
            最近5分钟无在线访客
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { statsApi, websiteApi } from '../api';
import dayjs from 'dayjs';

export default {
  name: 'RealtimeScreen',
  data() {
    return {
      websiteId: 0,
      websites: [],
      overview: {},
      recentList: [],
      hotPages: [],
      topReferers: [],
      trendMinutes: 30,
      refreshInterval: 5000,
      timer: null,
      currentTime: '',
      isConnected: false,
      charts: {},
      lastMaxId: 0,
      newItemIds: new Set(),
      blockedIps: new Set(),
      onlineDialogVisible: false,
      onlineLoading: false,
      onlineVisitors: [],
      onlineQueryTime: '',
    };
  },
  computed: {
    yesterdayCompareVisits() {
      const today = this.overview.today_visits || 0;
      const yesterday = this.overview.yesterday_visits || 0;
      if (yesterday === 0) return '+0%';
      const diff = ((today - yesterday) / yesterday * 100).toFixed(1);
      return (diff >= 0 ? '+' : '') + diff + '%';
    }
  },
  mounted() {
    this.loadWebsites();
    this.loadBlockedIps();
    this.startTimer();
    this.updateTime();
    setInterval(() => this.updateTime(), 1000);
    window.addEventListener('resize', this.resizeCharts);
  },
  beforeDestroy() {
    this.stopTimer();
    window.removeEventListener('resize', this.resizeCharts);
    Object.values(this.charts).forEach(c => c && c.dispose());
  },
  methods: {
    async loadWebsites() {
      try {
        const res = await websiteApi.list({ page: 1, page_size: 100 });
        if (res.code === 0) this.websites = res.data.list;
      } catch (e) { console.error(e); }
    },
    async loadBlockedIps() {
      try {
        const res = await statsApi.getBlockedIps();
        if (res.code === 0) this.blockedIps = new Set(res.data || []);
      } catch (e) { console.error(e); }
    },
    updateTime() {
      this.currentTime = dayjs().format('YYYY-MM-DD HH:mm:ss');
    },
    startTimer() {
      this.stopTimer();
      this.refreshAll();
      if (this.refreshInterval > 0) {
        this.timer = setInterval(() => this.refreshAll(), this.refreshInterval);
      }
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    onIntervalChange() {
      this.startTimer();
    },
    async refreshAll() {
      const params = { website_id: this.websiteId };
      try {
        await Promise.all([
          this.loadOverview(params),
          this.loadTrend(),
          this.loadRecent(params),
          this.loadLocation(params),
          this.loadHotPages(params),
          this.loadReferers(params),
          this.loadDevice(params),
        ]);
        this.isConnected = true;
      } catch (e) {
        this.isConnected = false;
        console.error(e);
      }
    },
    async loadOverview(params) {
      try {
        const res = await statsApi.realtimeOverview(params);
        if (res.code === 0) this.overview = res.data;
      } catch (e) { console.error(e); }
    },
    async loadTrend() {
      try {
        const res = await statsApi.realtimeTrend({ website_id: this.websiteId, minutes: this.trendMinutes });
        if (res.code !== 0) return;
        const d = res.data;
        this.$nextTick(() => {
          const el = this.$refs.trendChart;
          if (!el) return;
          if (!this.charts.trend) {
            this.charts.trend = echarts.init(el);
          }
          this.charts.trend.setOption({
            tooltip: { trigger: 'axis', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
            legend: { data: ['访问量', '独立访客'], top: 5, textStyle: { fontSize: 12, color: '#94a3b8' } },
            grid: { left: 50, right: 20, top: 45, bottom: 30 },
            xAxis: { type: 'category', data: d.labels, axisLabel: { fontSize: 11, color: '#64748b' }, axisLine: { lineStyle: { color: '#334155' } } },
            yAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#1e293b' } } },
            series: [
              { name: '访问量', type: 'line', smooth: true, data: d.visits, itemStyle: { color: '#5b8def' }, lineStyle: { width: 2 }, areaStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'rgba(91,141,239,0.2)'},{offset:1,color:'rgba(91,141,239,0)'}]) } },
              { name: '独立访客', type: 'line', smooth: true, data: d.visitors, itemStyle: { color: '#6b9e72' }, lineStyle: { width: 2 } },
            ]
          });
        });
      } catch (e) { console.error(e); }
    },
    async loadRecent(params) {
      try {
        const res = await statsApi.realtimeRecent({ ...params, limit: 30 });
        if (res.code !== 0) return;
        const newList = res.data.list;
        // 标记新出现的条目
        const oldIds = new Set(this.recentList.map(i => i.id));
        this.newItemIds = new Set();
        newList.forEach(item => {
          if (!oldIds.has(item.id) && item.id > this.lastMaxId) {
            this.newItemIds.add(item.id);
          }
        });
        if (newList.length > 0) {
          this.lastMaxId = Math.max(this.lastMaxId, newList[0].id);
        }
        this.recentList = newList;
        // 3秒后移除新条目标记
        setTimeout(() => { this.newItemIds = new Set(); }, 3000);
      } catch (e) { console.error(e); }
    },
    async loadLocation(params) {
      try {
        const res = await statsApi.realtimeLocation({ ...params, limit: 12 });
        if (res.code !== 0) return;
        const data = res.data;
        this.$nextTick(() => {
          const el = this.$refs.locationChart;
          if (!el) return;
          if (!this.charts.location) {
            this.charts.location = echarts.init(el);
          }
          this.charts.location.setOption({
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
            grid: { left: 80, right: 20, top: 10, bottom: 10 },
            xAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#1e293b' } } },
            yAxis: { type: 'category', data: data.map(d => d.location).reverse(), axisLabel: { fontSize: 11, color: '#94a3b8' }, axisLine: { lineStyle: { color: '#334155' } } },
            series: [{
              type: 'bar',
              data: data.map(d => d.visit_count).reverse(),
              barMaxWidth: 16,
              itemStyle: { color: new echarts.graphic.LinearGradient(1,0,0,0,[{offset:0,color:'#5b8def'},{offset:1,color:'rgba(91,141,239,0.2)'}]), borderRadius: [0,3,3,0] }
            }]
          }, true);
        });
      } catch (e) { console.error(e); }
    },
    async loadHotPages(params) {
      try {
        const res = await statsApi.realtimeHotPages({ ...params, limit: 8 });
        if (res.code === 0) this.hotPages = res.data;
      } catch (e) { console.error(e); }
    },
    async loadReferers(params) {
      try {
        const res = await statsApi.referer({ ...params, limit: 8 });
        if (res.code === 0) this.topReferers = res.data;
      } catch (e) { console.error(e); }
    },
    async loadDevice(params) {
      try {
        const res = await statsApi.device(params);
        if (res.code !== 0) return;
        const { devices } = res.data;
        const colors = ['#5b8def', '#6b9e72', '#d4905a'];
        this.$nextTick(() => {
          const el = this.$refs.deviceChart;
          if (!el) return;
          if (!this.charts.device) {
            this.charts.device = echarts.init(el);
          }
          this.charts.device.setOption({
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
            legend: { bottom: 0, textStyle: { fontSize: 11, color: '#94a3b8' } },
            series: [{
              type: 'pie', radius: ['35%', '60%'], center: ['50%', '40%'],
              data: devices.map((d, i) => ({ name: d.device, value: +d.count, itemStyle: { color: colors[i % colors.length] } })),
              label: { fontSize: 11, color: '#94a3b8', formatter: '{b}\n{d}%' },
              itemStyle: { borderRadius: 5, borderColor: '#0f172a', borderWidth: 2 },
            }]
          }, true);
        });
      } catch (e) { console.error(e); }
    },
    isNewItem(id) {
      return this.newItemIds.has(id);
    },
    formatUrl(url) {
      if (!url) return '-';
      try {
        const u = new URL(url);
        return u.pathname === '/' ? u.host + '/' : u.pathname;
      } catch {
        return url.length > 30 ? url.slice(0, 27) + '...' : url;
      }
    },
    formatTime(t) {
      if (!t) return '';
      return dayjs(t).format('HH:mm:ss');
    },
    getDeviceIcon(device) {
      const d = (device || '').toLowerCase();
      if (d.includes('mobile') || d.includes('phone')) return 'el-icon-mobile-phone';
      if (d.includes('tablet') || d.includes('pad')) return 'el-icon-mobile';
      return 'el-icon-monitor';
    },
    resizeCharts() {
      Object.values(this.charts).forEach(c => c && c.resize());
    },
    async showOnlineDialog() {
      this.onlineDialogVisible = true;
      this.onlineLoading = true;
      this.onlineVisitors = [];
      try {
        const res = await statsApi.realtimeOnlineVisitors({ website_id: this.websiteId, minutes: 5 });
        if (res.code === 0) {
          this.onlineVisitors = res.data.list;
          this.onlineQueryTime = res.data.query_time;
        }
      } catch (e) { console.error(e); }
      this.onlineLoading = false;
    },
    formatFullTime(t) {
      if (!t) return '';
      return dayjs(t).format('MM-DD HH:mm:ss');
    },
  }
};
</script>

<style scoped>
.realtime-screen {
  height: 100vh;
  background: #0f172a;
  color: #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.screen-header {
  height: 64px;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  border-bottom: 1px solid #1e293b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
}
.header-left, .header-right { display: flex; align-items: center; width: 280px; }
.header-right { justify-content: flex-end; }
.header-center { text-align: center; flex: 1; }
.screen-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  background: linear-gradient(90deg, #5b8def, #6b9e72);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 4px;
}
.screen-time {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}
.status-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #64748b;
  margin-right: 6px;
  animation: pulse 2s infinite;
}
.status-dot.active { background: #6b9e72; }
.status-text { font-size: 13px; color: #94a3b8; }
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.screen-body {
  flex: 1;
  display: flex;
  gap: 14px;
  padding: 14px;
  overflow: hidden;
}
.screen-left, .screen-right {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.screen-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.metric-row {
  display: flex;
  gap: 12px;
}
.metric-card {
  flex: 1;
  background: linear-gradient(135deg, rgba(91,141,239,0.08) 0%, rgba(30,41,59,0.6) 100%);
  border: 1px solid rgba(91,141,239,0.15);
  border-radius: 6px;
  padding: 14px 16px;
  position: relative;
  overflow: hidden;
}
.metric-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0;
  width: 3px; height: 100%;
  background: #5b8def;
}
.metric-value {
  font-size: 26px;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
  font-family: 'SF Mono', Consolas, monospace;
}
.metric-value.highlight { color: #6b9e72; }
.metric-label {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}
.metric-sub {
  font-size: 11px;
  color: #64748b;
  margin-top: 6px;
}
.metric-card.clickable {
  cursor: pointer;
  transition: all 0.2s;
}
.metric-card.clickable:hover {
  border-color: rgba(107,158,114,0.4);
  background: linear-gradient(135deg, rgba(107,158,114,0.08) 0%, rgba(30,41,59,0.6) 100%);
}
.metric-card.clickable:hover::before {
  background: #6b9e72;
}

.panel-card {
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid #1e293b;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.panel-header {
  padding: 10px 16px;
  border-bottom: 1px solid #1e293b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #cbd5e1;
  position: relative;
  padding-left: 10px;
}
.panel-title::before {
  content: '';
  position: absolute;
  left: 0; top: 50%;
  transform: translateY(-50%);
  width: 3px; height: 14px;
  background: #5b8def;
  border-radius: 2px;
}
.panel-body {
  flex: 1;
  padding: 12px;
  min-height: 0;
  overflow: hidden;
}
.chart-area { width: 100%; height: 100%; }
.trend-panel { flex: 1.2; min-height: 240px; }
.trend-chart { height: 100%; }
.flow-panel { flex: 1; min-height: 0; }
.flow-list { overflow-y: auto; padding: 8px 12px; }
.flow-list::-webkit-scrollbar { width: 4px; }
.flow-list::-webkit-scrollbar-track { background: #1e293b; }
.flow-list::-webkit-scrollbar-thumb { background: #475569; border-radius: 2px; }

.flow-item {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #1e293b;
  transition: background 0.3s;
}
.flow-item:last-child { border-bottom: none; }
.flow-item.new {
  background: rgba(107, 158, 114, 0.1);
  margin: 0 -4px;
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 4px;
  animation: slideIn 0.5s ease;
}
@keyframes slideIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.flow-avatar {
  width: 32px; height: 32px;
  border-radius: 6px;
  background: rgba(91,141,239,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5b8def;
  font-size: 16px;
  flex-shrink: 0;
}
.flow-info { flex: 1; min-width: 0; }
.flow-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.flow-ip {
  font-size: 12px;
  font-weight: 500;
  color: #e2e8f0;
  font-family: 'SF Mono', Consolas, monospace;
}
.flow-location {
  font-size: 11px;
  color: #64748b;
}
.flow-tag { transform: scale(0.85); transform-origin: left; }
.flow-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.flow-page {
  font-size: 11px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  flex: 1;
  line-height: 1.4;
}
.flow-time {
  font-size: 11px;
  color: #64748b;
  flex-shrink: 0;
  font-family: 'SF Mono', Consolas, monospace;
}

.panel-tabs { display: flex; gap: 4px; }
.panel-tabs span {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 3px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}
.panel-tabs span.active {
  background: rgba(91,141,239,0.15);
  color: #5b8def;
}
.flow-count {
  font-size: 11px;
  color: #64748b;
}

.hot-pages { display: flex; flex-direction: column; gap: 6px; }
.hot-page-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.hot-page-rank {
  width: 20px; height: 20px;
  border-radius: 4px;
  background: #334155;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hot-page-rank.rank-1 { background: #c76b6b; color: #fff; }
.hot-page-rank.rank-2 { background: #d4905a; color: #fff; }
.hot-page-rank.rank-3 { background: #c4a857; color: #fff; }
.hot-page-url {
  flex: 1;
  font-size: 12px;
  color: #cbd5e1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hot-page-count {
  font-size: 12px;
  color: #5b8def;
  font-weight: 600;
  font-family: 'SF Mono', Consolas, monospace;
}

.referer-list { display: flex; flex-direction: column; gap: 4px; }
.referer-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
}
.referer-rank {
  width: 18px;
  font-size: 11px;
  color: #64748b;
  text-align: center;
}
.referer-domain {
  flex: 1;
  font-size: 12px;
  color: #cbd5e1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.referer-count {
  font-size: 12px;
  color: #94a3b8;
  font-family: 'SF Mono', Consolas, monospace;
}

.device-chart { height: 180px; }

.empty-tip {
  text-align: center;
  color: #475569;
  font-size: 12px;
  padding: 30px 0;
}
</style>

<style>
.realtime-screen .el-select .el-input__inner {
  background: rgba(30,41,59,0.8);
  border-color: #334155;
  color: #e2e8f0;
}
.realtime-screen .el-select .el-input__inner::placeholder {
  color: #64748b;
}
.realtime-screen .el-select .el-input .el-select__caret {
  color: #64748b;
}
.realtime-screen .el-select .el-input:hover .el-input__inner {
  border-color: #475569;
}
.el-select-dropdown.realtime-dark-popper {
  background: #1e293b;
  border: 1px solid #334155;
}
.el-select-dropdown.realtime-dark-popper .el-select-dropdown__item {
  color: #94a3b8;
}
.el-select-dropdown.realtime-dark-popper .el-select-dropdown__item:hover {
  background: rgba(91,141,239,0.1);
  color: #e2e8f0;
}
.el-select-dropdown.realtime-dark-popper .el-select-dropdown__item.selected {
  color: #5b8def;
  font-weight: 600;
}
.el-select-dropdown.realtime-dark-popper .popper__arrow::after {
  border-bottom-color: #1e293b;
}
.el-select-dropdown.realtime-dark-popper .popper__arrow {
  border-bottom-color: #334155;
}

.online-dialog {
  background: #1e293b !important;
  border: 1px solid #334155;
  border-radius: 8px;
}
.online-dialog .el-dialog__header {
  border-bottom: 1px solid #334155;
  padding: 16px 20px;
}
.online-dialog .el-dialog__title {
  color: #e2e8f0;
  font-size: 15px;
  font-weight: 600;
}
.online-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #64748b;
}
.online-dialog .el-dialog__headerbtn:hover .el-dialog__close {
  color: #e2e8f0;
}
.online-dialog .el-dialog__body {
  padding: 0;
}
.online-dialog-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  font-size: 12px;
  color: #64748b;
  border-bottom: 1px solid #1e293b;
}
.online-dialog-time { font-family: 'SF Mono', Consolas, monospace; }
.online-dialog-list {
  max-height: 480px;
  overflow-y: auto;
  padding: 8px 20px;
}
.online-dialog-list::-webkit-scrollbar { width: 4px; }
.online-dialog-list::-webkit-scrollbar-track { background: #1e293b; }
.online-dialog-list::-webkit-scrollbar-thumb { background: #475569; border-radius: 2px; }

.online-visitor-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #1e293b;
}
.online-visitor-item:last-child { border-bottom: none; }
.online-visitor-avatar {
  width: 36px; height: 36px;
  border-radius: 8px;
  background: rgba(91,141,239,0.12);
  display: flex; align-items: center; justify-content: center;
  color: #5b8def; font-size: 18px;
  flex-shrink: 0;
}
.online-visitor-info { flex: 1; min-width: 0; }
.online-visitor-top {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 4px;
}
.online-visitor-ip {
  font-size: 13px; font-weight: 500; color: #e2e8f0;
  font-family: 'SF Mono', Consolas, monospace;
}
.online-visitor-location { font-size: 11px; color: #64748b; }
.online-visitor-count {
  font-size: 11px; color: #5b8def;
  background: rgba(91,141,239,0.1);
  padding: 2px 8px; border-radius: 3px;
}
.online-visitor-bottom {
  display: flex; justify-content: space-between; align-items: center;
  gap: 10px; margin-bottom: 4px;
}
.online-visitor-page {
  font-size: 12px; color: #94a3b8;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;
}
.online-visitor-time {
  font-size: 11px; color: #64748b; flex-shrink: 0;
  font-family: 'SF Mono', Consolas, monospace;
}
.online-visitor-meta { font-size: 11px; color: #475569; }
.online-empty {
  text-align: center; padding: 40px 0;
  color: #475569; font-size: 13px;
}
</style>
